import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';

type MessageType = {
    isOpen: boolean;
    duration?: number;
    message?: any;
    subMessage?: any;
    isUnauthorized?: boolean;
    extraArgs?: any;
    type?: any;
};

const commonSlice = createSlice({
    name: 'common',
    initialState: {
        urlSuffix: '',
        message: {
            isOpen: false,
            duration: 0,
            message: '',
        },
        isLoader: false,
        isSuperRealm: false,
        isAdminRealm: false,
        selectedNodeLoader: true,
        selectedTabAdminPortal: '',
        selectedNode: undefined,
        pageHeader: {},
        tabKey: undefined,
        selectedRealm: undefined,
        xRealm: undefined,
        appHeaderTitle: undefined,
        selectedRealmNode: undefined,
        appHeaderSubTitle: undefined,
        appTitle: '',
        applicationColor: '',
        isDirtyForm: false,
        confirmDirtyForm: false,
        isDrawerOpen: true,
        drawerBottomsheeetOpen: false,
        drawerSwitchOrg: false,
        authModal: false,
        logout: false,
        mqttCredentials: {},
        selectedOrgNameServiceAdminPortal: '',
        lastInteractionTime: '',
        deleteConfirmationKey: '',
        headerColor: '#007BC1',
        timelineStatusColor: '',
        deviceSideBar: {
            isEnable: false,
            sideBarOpen: false,
            selectedIds: [],
        },
        snoozeDuration: '',
        mobilePermissions: [],
        globalPermissions: [],
        googleMapLoaded: false,
        internalPageHeader: {
            bottomSheetOpen: false,
            assignRoleUser: false,
            selectedPageHeader: false,
            headerCount: 0,
        },
        mqttConnectionStatus: false,
        needsUpdate: false,
        googleApiKey: '',
        drawerHeaderTranslationKey: 'COMMON:ALL_GROUPS',
        deviceHeaderStatus: undefined,
        settingsLogo: null,
        sortOption: '',
        googleMapError: undefined,
        isLocalizationCount: 0,
        isInviteFlow: false,
        deassignUserPayload: {
            assignType: 'REMOVE',
            userIds: [],
        },
        assignUsersPayload: {
            assignType: 'ASSIGN',
            groupIds: [],
            userIds: [],
        },
        filteredList: [],
        filterUserList: [],
        canEditSubscription: false,
        tabMainOrTrial: 'main', // used to determine if the tab is for main or trial feature package Upgrade/Downgrade Entitlements
        isUpcomingEmpty: true,
        isLocked: false,
        tabsList: [],
        redundancyCount: 0,
        loadGroupPayload: {},
        createdActions: [],
        loadGroupsItem: {} as any,
        loadOn: [],
        loadGroupOn: [],
        tempLoadGroupActions: [],
        disableHeaderBackNavigation: false,
        allTempActionsList: [],
        isLoadRefresh: false,
    },
    reducers: {
        setSortOption: (state, action: PayloadAction<string>) => {
            state.sortOption = action.payload;
        },
        setIsUpcomingEmpty: (state, action: PayloadAction<boolean>) => {
            state.isUpcomingEmpty = action.payload;
        },
        setMessageContent: (state: { message: MessageType }, action: PayloadAction<MessageType>) => {
            state.message = action.payload;
        },
        toggleLoader: (state, action: PayloadAction<boolean>) => {
            state.isLoader = action.payload;
        },
        setDrawerHeaderTranslationKey: (state, action: PayloadAction<any>) => {
            state['drawerHeaderTranslationKey'] = action.payload;
        },
        setSelectedNodeLoader: (state, action: PayloadAction<any>) => {
            state.selectedNodeLoader = action.payload;
        },
        setSelectedNode: (state, action: PayloadAction<any>) => {
            state.selectedNode = action.payload;
        },
        setSelectedTabAdminPortal: (state, action: PayloadAction<any>) => {
            state.selectedTabAdminPortal = action.payload;
        },
        setPageHeaderData: (state, action: PayloadAction<any>) => {
            state.pageHeader = action.payload;
        },
        setAppHeaderTitle: (state, action: PayloadAction<any>) => {
            state.appHeaderTitle = action.payload;
        },
        setRealmNode: (state, action: PayloadAction<any>) => {
            state.selectedRealmNode = action.payload;
        },
        setTabKey: (state: RootState, action: PayloadAction<any>) => {
            state['tabKey'] = action.payload;
        },
        setMqttCredentials: (state: RootState, action: PayloadAction<{ [key: string]: any }>) => {
            state['mqttCredentials'] = action.payload;
        },
        setAppHeaderSubTitle: (state, action: PayloadAction<any>) => {
            state.appHeaderSubTitle = action.payload;
        },
        setAppTitle: (state, action: PayloadAction<any>) => {
            state.appTitle = action.payload;
        },
        setSelectedRealm: (state, action: PayloadAction<any>) => {
            state.selectedRealm = action.payload;
        },
        setXRealm: (state, action: PayloadAction<any>) => {
            state.xRealm = action.payload;
        },
        setApplicationColor: (state, action: PayloadAction<any>) => {
            state.applicationColor = action.payload;
        },
        setIsSuperRealm: (state, action: PayloadAction<any>) => {
            state.isSuperRealm = action.payload;
        },
        setIsAdminRealm: (state, action: PayloadAction<any>) => {
            state.isAdminRealm = action.payload;
        },
        setIsDirtyForm: (state, action: PayloadAction<boolean>) => {
            state.isDirtyForm = action.payload;
        },
        setConfirmDirtyForm: (state, action: PayloadAction<boolean>) => {
            state.confirmDirtyForm = action.payload;
        },
        setIsDrawerOpen: (state, action: PayloadAction<boolean>) => {
            state.isDrawerOpen = action.payload;
        },
        setDrawerBottomsheetOpen: (state, action: PayloadAction<boolean>) => {
            state.drawerBottomsheeetOpen = action.payload;
        },
        setDrawerSwitchOrg: (state, action: PayloadAction<boolean>) => {
            state.drawerSwitchOrg = action.payload;
        },
        setAuthModal: (state, action: PayloadAction<any>) => {
            state.authModal = action.payload;
        },
        setLogout: (state, action: PayloadAction<any>) => {
            state.logout = action.payload;
        },
        setSelectedOrgNameServiceAdminPortal: (state, action: PayloadAction<string>) => {
            state.selectedOrgNameServiceAdminPortal = action.payload;
        },
        setLastInteractionTime: (state, action: PayloadAction<any>) => {
            state.lastInteractionTime = action.payload;
        },
        setDeleteConfirmationKey: (state, action) => {
            state.deleteConfirmationKey = action.payload;
        },
        resetAlarmDetailsHeaderColor: (state, action: PayloadAction<any>) => {
            state.headerColor = action.payload;
        },
        setDeviceSideBar: (state, action) => {
            state.deviceSideBar = action.payload;
        },
        setSnoozeDuration: (state, action) => {
            state.snoozeDuration = action.payload;
        },
        setNewMobileGlobalPermission: (state) => {
            if (state.globalPermissions?.length && state.mobilePermissions?.length) {
                state.globalPermissions = state.mobilePermissions;
            }
        },
        setMobileResetPermissions: (state) => {
            if (state.mobilePermissions.length && state.globalPermissions.length) {
                state.mobilePermissions = state.globalPermissions;
                state.globalPermissions = [];
            }
            return;
        },

        setMobilePermissions: (state, action) => {
            if (state.mobilePermissions.length && !state.globalPermissions.length) {
                state.globalPermissions = state.mobilePermissions;
            }
            const permissions: any = action.payload;
            state.mobilePermissions = permissions;
        },
        setGoogleMapLoaded: (state, action) => {
            state.googleMapLoaded = action.payload;
        },
        setInternalPageHeader: (state: RootState, action: PayloadAction<any>) => {
            state['internalPageHeader'][action.payload.key] = action.payload.value;
        },
        setTimelineStatusColor: (state: RootState, action: PayloadAction<any>) => {
            state.timelineStatusColor = action.payload;
        },
        setMqttConnectionStatus: (state: RootState, action: PayloadAction<any>) => {
            state['mqttConnectionStatus'] = action.payload;
        },
        setNeedsUpdate: (state, action: PayloadAction<any>) => {
            state.needsUpdate = action.payload;
        },
        setGoogleApiKey: (state, action: PayloadAction<any>) => {
            state.googleApiKey = action.payload;
        },
        setDeviceHeaderStatus: (state, action: PayloadAction<any>) => {
            state.deviceHeaderStatus = action.payload;
        },
        setSettingsLogo: (state, action: PayloadAction<any>) => {
            state.settingsLogo = action.payload;
        },
        setGoogleMapError: (state, action) => {
            state.googleMapError = action.payload;
        },
        setIsLocalizationLoaded: (state, action) => {
            state.isLocalizationCount = action.payload;
        },
        setIsInviteFlow: (state, action: PayloadAction<boolean>) => {
            state.isInviteFlow = action.payload;
        },
        setDeassignUserPayload: (state, action) => {
            state.deassignUserPayload = action.payload;
        },
        resetDeassignUserPayload: (state) => {
            state.deassignUserPayload = {
                assignType: 'REMOVE',
                userIds: [],
            };
        },
        setAssignUsersPayload: (state, action) => {
            state.assignUsersPayload = action.payload;
        },
        resetAssignUsersPayload: (state) => {
            state.assignUsersPayload = {
                assignType: 'ASSIGN',
                groupIds: [],
                userIds: [],
            };
        },
        setFilteredList: (state, action) => {
            state.filteredList = action.payload;
        },
        resetFilteredList: (state) => {
            state.filteredList = [];
        },
        setFilterUserList: (state, action) => {
            state.filterUserList = action.payload;
        },
        resetFilterUserList: (state) => {
            state.filterUserList = [];
        },
        setCanEditSubscription: (state, action: PayloadAction<any>) => {
            state.canEditSubscription = action.payload;
        },
        setTabMainOrTrial: (state, action: PayloadAction<any>) => {
            state.tabMainOrTrial = action.payload;
        },
        setTabsList: (state, action) => {
            state.tabsList = action.payload;
        },
        setRedundancyCount: (state, action) => {
            state.redundancyCount = action.payload;
        },
        setLoadGroupPayload: (state, action) => {
            state.loadGroupPayload = action.payload;
        },
        setCreatedActions: (state, action: PayloadAction<any>) => {
            state.createdActions = action.payload;
        },
        resetCreatedActions: (state) => {
            state.createdActions = [];
        },
        setLoadGroupsItem: (state, action) => {
            state.loadGroupsItem = action.payload;
        },
        resetLoadGroupsItem: (state) => {
            state.loadGroupsItem = null;
        },

        setLoadOn: (state, action: PayloadAction<any>) => {
            state.loadOn = action.payload;
        },
        setLoadGroupOn: (state, action: PayloadAction<any>) => {
            state.loadGroupOn = action.payload;
        },
        setTempLoadGroupActions: (state, action: PayloadAction<any>) => {
            state.tempLoadGroupActions = action.payload;
        },
        resetTempLoadGroupActions: (state) => {
            state.tempLoadGroupActions = [];
        },
        setAllTempActionsList: (state, action: PayloadAction<any>) => {
            state.allTempActionsList = action.payload;
        },
        setDisableHeaderBackNavigation: (state, action: PayloadAction<boolean>) => {
            state.disableHeaderBackNavigation = action.payload;
        },
        setIsLoadRefresh: (state, action: PayloadAction<boolean>) => {
            state.isLoadRefresh = action.payload;
        },
    },
});

export const {
    setIsUpcomingEmpty,
    setSelectedNode,
    setMessageContent,
    setIsSuperRealm,
    setIsAdminRealm,
    setSelectedTabAdminPortal,
    setTabKey,
    toggleLoader,
    setMqttCredentials,
    setSelectedRealm,
    setXRealm,
    setPageHeaderData,
    setAppHeaderTitle,
    setAppTitle,
    setIsDirtyForm,
    setConfirmDirtyForm,
    setSelectedNodeLoader,
    setApplicationColor,
    setIsDrawerOpen,
    setDrawerBottomsheetOpen,
    setDrawerSwitchOrg,
    setAppHeaderSubTitle,
    setAuthModal,
    setDrawerHeaderTranslationKey,
    setLogout,
    setSelectedOrgNameServiceAdminPortal,
    setRealmNode,
    setLastInteractionTime,
    setDeleteConfirmationKey,
    resetAlarmDetailsHeaderColor,
    setDeviceSideBar,
    setSnoozeDuration,
    setMobilePermissions,
    setMobileResetPermissions,
    setGoogleMapLoaded,
    setInternalPageHeader,
    setTimelineStatusColor,
    setMqttConnectionStatus,
    setNeedsUpdate,
    setGoogleApiKey,
    setDeviceHeaderStatus,
    setSettingsLogo,
    setSortOption,
    setGoogleMapError,
    setIsLocalizationLoaded,
    setIsInviteFlow,
    setDeassignUserPayload,
    resetDeassignUserPayload,
    setAssignUsersPayload,
    resetAssignUsersPayload,
    setFilteredList,
    setFilterUserList,
    resetFilteredList,
    setCanEditSubscription,
    setTabMainOrTrial,
    setTabsList,
    setNewMobileGlobalPermission,
    setRedundancyCount,
    setLoadGroupPayload,
    setCreatedActions,
    setLoadGroupsItem,
    resetLoadGroupsItem,
    setLoadOn,
    setLoadGroupOn,
    setTempLoadGroupActions,
    resetTempLoadGroupActions,
    resetCreatedActions,
    setDisableHeaderBackNavigation,
    setAllTempActionsList,
    setIsLoadRefresh,
} = commonSlice.actions;

export default commonSlice;
